import { MlbStatsHelper } from 'classes/helpers/mlb-stats.helper';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { endOfToday, startOfDay, subDays, subWeeks } from 'date-fns';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { MachinePerformanceHelper } from 'lib_ts/classes/machine-performance.helper';
import { meanObject } from 'lib_ts/classes/math.utilities';
import { BallType } from 'lib_ts/enums/machine.enums';
import { MlbSportId } from 'lib_ts/enums/mlb-stats-api/base.enum';
import {
  BuildPriority,
  lookupPitchType,
  PitchType,
  tooltipPitchType,
} from 'lib_ts/enums/pitches.enums';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import { IPitchPerfFilters } from 'lib_ts/interfaces/data/i-pitch-performance';
import { IGroupedFires } from 'lib_ts/interfaces/i-pitch-performance';
import {
  IMlbPitch,
  IMlbPitchExt,
} from 'lib_ts/interfaces/mlb-stats-api/i-pitch';
import { IMlbPlayer } from 'lib_ts/interfaces/mlb-stats-api/i-player';
import { ITrajektRefBreak } from 'lib_ts/interfaces/pitches';
import { IAggregateMachineShotsDict } from 'lib_ts/interfaces/training/i-machine-shot';
import _, { isEqual } from 'lodash';
import { AdminMachinesService } from 'services/admin/machines.service';
import { MlbStatsService } from 'services/mlb-stats.service';
import { ShotsService } from 'services/shots.service';
import { create } from 'zustand';

export const ENABLE_COMPARISON_PLOTS = false;

// default report shows last 100 shots from the previous week
const DEFAULT_NUM_SHOTS = 100;
const DEFAULT_DELTA_WEEKS = 1;

const MAX_STATS_BREAKS_TO_PLOT = 20;

const getPitchGroups = (pitches: IMlbPitchExt[], groups: IGroupedFires[]) => {
  const dict = ArrayHelper.groupBy(pitches, 'type');

  return Object.keys(dict)
    .sort()
    .map((mlbType) => {
      const pitchType = lookupPitchType(mlbType);
      const refPitch = groups.find((g) => g.type === pitchType);

      const breaks = dict[mlbType]
        // only keep valid guids
        .filter((guid) => MlbStatsHelper.getErrors(guid).length === 0)
        // breaks prio b/c these will be used for breaks plot
        .map((guid) =>
          MlbStatsHelper.convertToChars(guid, BuildPriority.Breaks, true)
        )
        // only keep non-empty results
        .filter((m) => m.chars?.breaks)
        // cast for TS
        .map((m) => m.chars?.breaks as ITrajektRefBreak)
        .filter((_, i) => i < MAX_STATS_BREAKS_TO_PLOT);

      const avgBreak = meanObject(breaks);

      const o: IPitchGroup = {
        pitchID: refPitch?._id ?? 'none',
        type: pitchType,
        typeName: tooltipPitchType(pitchType),
        breaks: breaks,
        avgBreak: avgBreak,
        maxBreakDistanceIn: avgBreak
          ? _.max(
              breaks.map((b) => {
                const xDelta = b.xInches - avgBreak.xInches;
                const zDelta = b.zInches - avgBreak.zInches;
                return Math.sqrt(Math.pow(xDelta, 2) + Math.pow(zDelta, 2));
              })
            ) ?? 0
          : 0,

        rgb: refPitch?.rgb ?? '100,100,100',
        invisible: !refPitch,
      };

      return o;
    })
    .sort((a, b) => {
      if (a.invisible === b.invisible) {
        // sort alphabetically
        return a.typeName.localeCompare(b.typeName);
      }

      // put invisible stuff last
      return a.invisible ? 1 : -1;
    });
};

interface IPitchPerformanceState {
  loading: boolean;

  allGroups: IGroupedFires[];
  prevGroups: IGroupedFires[];

  // first group of each pitch type (for statsPitchGroups to use as ref)
  firstGroups: IGroupedFires[];

  // for filtering data from the server
  filters: IPitchPerfFilters;

  machineID: string;
  selectedPitchId: string;
  selectedShotIds: string[];

  isAveraged: boolean;
  showThresholds: boolean;
  showTargets: boolean;

  aggDict: IAggregateMachineShotsDict;
}

interface IPitchPerformanceActions {
  readonly setFirstGroups: (groups: IGroupedFires[]) => void;
  readonly mergeFilters: (value: Partial<IPitchPerfFilters>) => void;
  readonly setSelectedPitchId: (value: string) => void;
  readonly setSelectedShotIds: (value: string[]) => void;
  readonly initialize: (machineId: string, ballType?: BallType) => void;
  readonly generate: () => Promise<void>;
  readonly reset: () => void;

  readonly setIsAveraged: (value: boolean) => void;
  readonly setShowThresholds: (value: boolean) => void;
  readonly setShowTargets: (value: boolean) => void;
}

interface IPitchGroup {
  pitchID: string;
  type: PitchType;
  typeName: string;

  breaks: ITrajektRefBreak[];
  avgBreak: ITrajektRefBreak | undefined;
  maxBreakDistanceIn: number;

  // color of the first (checked) pitch in the data with the same pitch type
  rgb: string;
  invisible: boolean;
}

interface IStatsApiState {
  statsDialog?: number;

  statsLoading: boolean;

  statsSeason?: number;
  statsLeague?: MlbSportId;

  statsPitcherPk?: number;

  // loaded and set whenever we have season, league, and pitcher specified
  statsPitchGroups: IPitchGroup[];
  cachedStatsPitches: IMlbPitch[];

  statsSeasonOptions: IOption[];
  statsLeagueOptions: IOption[];

  // for rendering the pitcher details like hand, team name, etc...
  statsPitchers: IMlbPlayer[];
}

interface IStatsApiActions {
  readonly setStatsDialog: (v: number | undefined) => void;
  readonly fetchStatsSeasons: () => void;
  readonly fetchStatsPitchers: () => void;
  readonly fetchStatsPitches: (
    groups: IGroupedFires[],
    useCached: boolean
  ) => void;

  readonly setStatsSeason: (value: number | undefined) => void;
  // * not currently used, assumed to be always MLB
  readonly setStatsLeague: (value: MlbSportId | undefined) => void;
  readonly setStatsPitcherPk: (value: number | undefined) => void;
}

type IPitchPerformanceStore = IPitchPerformanceState &
  IPitchPerformanceActions &
  IStatsApiState &
  IStatsApiActions;

const generateDefaultFilters = (): IPitchPerfFilters => {
  const currEnd = endOfToday();
  const currStart = startOfDay(subWeeks(currEnd, DEFAULT_DELTA_WEEKS));

  const prevEnd = subDays(currStart, 1);
  const prevStart = startOfDay(subWeeks(prevEnd, DEFAULT_DELTA_WEEKS));

  return {
    comparing: true,

    currPeriod: {
      start: currStart.toISOString(),
      end: currEnd.toISOString(),
    },
    prevPeriod: {
      start: prevStart.toISOString(),
      end: prevEnd.toISOString(),
    },
    shots: DEFAULT_NUM_SHOTS,
    // ensures that the ball_type changed detection works on init
    ball_type: undefined,
    training: false,
  };
};

const initialState: IPitchPerformanceState & IStatsApiState = {
  loading: false,

  allGroups: [],
  prevGroups: [],
  firstGroups: [],

  filters: generateDefaultFilters(),
  machineID: '',
  selectedPitchId: '',
  selectedShotIds: [],

  isAveraged: true,
  showThresholds: false,
  showTargets: true,

  statsLoading: false,

  statsSeason: undefined,
  statsLeague: MlbSportId.MLB,
  statsPitcherPk: undefined,

  statsSeasonOptions: [],
  statsLeagueOptions: [],

  statsPitchers: [],

  statsPitchGroups: [],
  cachedStatsPitches: [],

  aggDict: {},
};

const usePitchPerformanceStore = create<IPitchPerformanceStore>((set, get) => ({
  ...initialState,

  setFirstGroups: (groups) => {
    const dict = ArrayHelper.groupBy(groups, 'type');
    set({
      firstGroups: Object.values(dict)
        .filter((g) => g.length > 0)
        .map((g) => g[0]),
    });
  },

  mergeFilters: async (value: Partial<IPitchPerfFilters>) => {
    const { filters: currentFilters, machineID } = get();

    const mergedFilters = { ...currentFilters, ...value };

    // because fetching the report is potentially expensive, we'll do a manual equality check here
    if (!isEqual(currentFilters, mergedFilters)) {
      // reload aggDict whenever ball type changes
      if (currentFilters.ball_type !== mergedFilters.ball_type) {
        const aggDict = await ShotsService.getInstance().getAggMatches({
          machineID: machineID,
          ball_type: mergedFilters.ball_type,
          includeLowConfidence: false,
          includeHitterPresent: false,
        });

        set({ aggDict });
      }

      set({ filters: mergedFilters });
    }
  },

  initialize: async (machineID: string, ballType?: BallType) => {
    const { mergeFilters } = get();

    set({ machineID });

    // init ball type filter with the current machine ball type
    mergeFilters({
      ball_type: ballType ?? initialState.filters.ball_type,
    });
  },

  generate: async () => {
    const { filters, machineID } = get();

    try {
      set({ loading: true });

      const results =
        await AdminMachinesService.getInstance().getPitchPerformance(
          machineID,
          filters
        );

      const allGroups = results
        ? MachinePerformanceHelper.getGroupsFromFires(results.current)
        : initialState.allGroups;

      const prevGroups = results?.previous
        ? MachinePerformanceHelper.getGroupsFromFires(results.previous)
        : initialState.prevGroups;

      set({
        allGroups,
        prevGroups,
        loading: false,
      });
    } catch (e) {
      console.error(e);

      NotifyHelper.error({
        message_md: `Unable to fetch pitch performance: ${e}`,
      });

      set({ loading: false });
    }
  },

  setSelectedPitchId: (value: string) => {
    set({ selectedPitchId: value });
  },

  setSelectedShotIds: (value: string[]) => {
    set({ selectedShotIds: value });
  },

  reset: () => set(initialState),

  setIsAveraged: (value: boolean) => {
    set({ isAveraged: value });
  },

  setShowThresholds: (value: boolean) => {
    set({ showThresholds: value });
  },

  setShowTargets: (value: boolean) => {
    set({ showTargets: value });
  },

  setStatsDialog: (v) => set({ statsDialog: v }),

  fetchStatsSeasons: async () => {
    const { statsLeague } = get();

    if (!statsLeague) {
      return;
    }

    set({
      statsLoading: true,
    });

    const seasons = await MlbStatsService.getInstance().countGames({
      sportId: statsLeague,
    });

    seasons.sort((a, b) => (a.season > b.season ? -1 : 1));

    const options = seasons.map((m) => {
      const o: IOption = {
        label: m.season.toString(),
        value: m.season.toString(),
        disabled: m.total === 0,
      };

      return o;
    });

    set({
      statsLoading: false,
      statsSeasonOptions: options,
    });
  },

  fetchStatsPitchers: async () => {
    const { statsSeason, statsLeague } = get();

    if (!statsSeason) {
      return;
    }

    if (!statsLeague) {
      return;
    }

    set({
      statsLoading: true,
    });

    const teams = await MlbStatsService.getInstance().getTeams({
      season: statsSeason,
      sportId: statsLeague,
    });

    const players = await MlbStatsService.getInstance().getPlayers({
      season: statsSeason,
      sportId: statsLeague,
      // teams are just provided so we mixin details like for the colors on avatar bgs
      teams: teams,
    });

    set({
      statsLoading: false,
      statsPitchers: players,
    });
  },

  fetchStatsPitches: async (groups, useCached) => {
    if (useCached) {
      const { cachedStatsPitches } = get();
      set({
        statsPitchGroups: getPitchGroups(cachedStatsPitches, groups),
      });
      return;
    }

    const { statsSeason, statsLeague, statsPitcherPk } = get();

    set({ statsLoading: true });

    const statsPitches =
      statsSeason && statsLeague && statsPitcherPk
        ? await MlbStatsService.getInstance().getPitches({
            sportId: statsLeague,
            season: statsSeason,
            pitcherPk: statsPitcherPk,
          })
        : [];

    set({
      statsLoading: false,
      cachedStatsPitches: statsPitches,
      statsPitchGroups: getPitchGroups(statsPitches, groups),
    });
  },

  setStatsSeason: (value: number | undefined) => {
    set({ statsSeason: value });
  },
  setStatsLeague: (value: MlbSportId | undefined) => {
    set({ statsLeague: value });
  },
  setStatsPitcherPk: async (value: number | undefined) => {
    set({ statsPitcherPk: value });
  },
}));

export default usePitchPerformanceStore;
